import { reactive, ref, computed, shallowRef } from 'vue'
import { useChatSocket } from './useChatSocket'
import { useChatAudio } from './useChatAudio'

/**
 * Singleton WebRTC call manager.
 *
 * Ties together three moving parts:
 *   1. The chat WS (call lifecycle frames: incoming/cancel/reject/ended)
 *   2. The mediasoup-server WS (media-plane signaling — SDP, ICE,
 *      produce/consume)
 *   3. `getUserMedia` / `getDisplayMedia` for local tracks
 *
 * The UI layer only interacts with the reactive `activeCall` state + the
 * imperative actions (`start`, `accept`, `reject`, `hangup`, `toggleMute`,
 * `toggleCamera`, `toggleScreenShare`). The mediasoup-client import is
 * dynamic so the WebRTC stack is only loaded when a user actually makes
 * or receives a call — keeps the initial chat bundle small.
 */

export type CallStatus =
    | 'idle'
    | 'outgoing'    // caller dialed, waiting for accept
    | 'incoming'    // callee is being rung
    | 'connecting'  // accepted, dialing mediasoup
    | 'active'      // media flowing
    | 'ending'      // hangup in progress

export interface IncomingCallInfo {
    callId: string
    threadId: string
    threadKind: 'dm' | 'group'
    kind: 'audio' | 'video'
    from: { id: string; name: string; avatar: string | null }
}

export interface RemotePeer {
    userId: string
    audioStream: MediaStream | null
    videoStream: MediaStream | null
    screenStream: MediaStream | null
    hasAudio: boolean
    hasVideo: boolean
    hasScreen: boolean
    /** Track which consumerIds map to which stream slot so a
     *  `consumerClosed` WS frame can detach the right track instead
     *  of leaving an orphan in the tile. */
    consumerIds: string[]
    /** consumerId → which slot the track lives in (audio/video/screen) */
    consumerSlot: Record<string, 'audio' | 'video' | 'screen'>
}

export interface ActiveCallState {
    callId: string
    threadId: string
    threadKind: 'dm' | 'group'
    kind: 'audio' | 'video'
    status: CallStatus
    /** Map<userId, RemotePeer>  */
    remotes: Map<string, RemotePeer>
    localStream: MediaStream | null
    screenStream: MediaStream | null
    muted: boolean
    cameraOff: boolean
    screenSharing: boolean
    startedAt: number
}

const incoming = ref<IncomingCallInfo | null>(null)
const active = shallowRef<ActiveCallState | null>(null)
const error = ref<string | null>(null)

let mediasoupWs: WebSocket | null = null
let mediasoupDevice: any = null   // lazy-imported mediasoup-client Device
let sendTransport: any = null
let recvTransport: any = null
let screenProducer: any = null    // mediasoup-client Producer for the screen share track
let rpcCounter = 0
const pending = new Map<number, { resolve: (v: any) => void; reject: (e: any) => void }>()
let wired = false
let autoRejectTimer: ReturnType<typeof setTimeout> | null = null
// Synchronous in-flight guard for start()/accept(). `active.value` is only
// set AFTER captureLocal resolves, so without this a rapid double-click on
// the call buttons would fire two parallel `getUserMedia` prompts.
let callStarting = false

const AUTO_REJECT_MS = 30_000

/* ───────────────── RPC helpers ───────────────── */

function rpc(method: string, payload: Record<string, any> = {}): Promise<any> {
    if (!mediasoupWs || mediasoupWs.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error('mediasoup_disconnected'))
    }
    const id = ++rpcCounter
    return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject })
        mediasoupWs!.send(JSON.stringify({ id, type: method, ...payload }))
        // Timeout stale RPCs after 10s
        setTimeout(() => {
            if (pending.has(id)) {
                pending.get(id)!.reject(new Error(`rpc_timeout:${method}`))
                pending.delete(id)
            }
        }, 10_000)
    })
}

/* ───────────────── Chat-WS lifecycle frames ───────────────── */

function wire() {
    if (wired) return
    wired = true
    const { on, send: sendChatFrame } = useChatSocket()
    const { startCallRingtone, stopCallRingtone } = useChatAudio()

    on('call.incoming', (frame) => {
        if (!frame?.callId || !frame?.threadId || !frame?.from) return
        // If we're already in a call OR ringing for another call, auto-reject
        // the new one so the new caller gets a clear `call.rejected` instead
        // of silently overwriting the first ringer.
        if ((active.value && active.value.status !== 'idle') || incoming.value) {
            sendChatFrame({ type: 'call.reject', callId: frame.callId, threadId: frame.threadId })
            return
        }
        incoming.value = {
            callId: frame.callId,
            threadId: frame.threadId,
            threadKind: frame.threadKind || 'dm',
            kind: frame.kind || 'audio',
            from: frame.from
        }
        // Start the ringtone (respects user's callSoundEnabled + volume).
        // Stopped on accept/reject/auto-reject/ended below.
        startCallRingtone().catch(() => { /* noop */ })
        // Auto-reject after 30 s if unanswered
        if (autoRejectTimer) clearTimeout(autoRejectTimer)
        autoRejectTimer = setTimeout(() => {
            if (incoming.value?.callId === frame.callId) reject()
        }, AUTO_REJECT_MS)
    })

    on('call.started', (frame) => {
        // Our own initiate was acknowledged — move to 'outgoing' (ringing)
        // or straight to 'connecting' if we already have an active call
        // slot reserved (optimistic UI did `start()` locally).
        if (!active.value) return
        if (active.value.callId !== frame.callId) return
        if (active.value.status === 'outgoing') return  // already ringing
        active.value = { ...active.value, status: 'outgoing' }
    })

    on('call.ended', (frame) => {
        // Either we were cancelled as a callee, or the peer hung up
        if (incoming.value?.callId === frame.callId) {
            incoming.value = null
            stopCallRingtone()
            if (autoRejectTimer) { clearTimeout(autoRejectTimer); autoRejectTimer = null }
        }
        if (active.value?.callId === frame.callId) {
            teardown()
        }
    })

    on('call.rejected', (frame) => {
        if (active.value?.callId === frame.callId && active.value.status === 'outgoing') {
            error.value = 'call_rejected'
            teardown()
        }
    })
}

/* ───────────────── Core lifecycle ───────────────── */

async function start(threadId: string, threadKind: 'dm' | 'group', kind: 'audio' | 'video') {
    wire()
    if (active.value || callStarting) throw new Error('already_in_call')
    callStarting = true
    error.value = null

    try {
        // Provision local tracks BEFORE sending initiate so the ring state is
        // truthful: if mic/cam permission is denied we don't ring anyone.
        const localStream = await captureLocal(kind)

        const callId = 'c_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
        const state: ActiveCallState = {
            callId,
            threadId,
            threadKind,
            kind,
            status: 'outgoing',
            remotes: new Map(),
            localStream,
            screenStream: null,
            muted: false,
            cameraOff: false,
            screenSharing: false,
            startedAt: Date.now()
        }
        active.value = state

        // Connect to mediasoup first; the callee will join once they accept.
        try {
            await connectMediasoup(threadId)
            await publishLocalTracks(localStream, kind)
        } catch (err: any) {
            console.error('[chat:call] mediasoup setup failed', err)
            error.value = err?.message || 'setup_failed'
            teardown()
            throw err
        }

        // Tell the chat WS to fan out `call.incoming` to the other participants
        useChatSocket().send({
            type: 'call.initiate',
            threadId,
            kind,
            callId
        })
    } finally {
        callStarting = false
    }
}

async function accept() {
    wire()
    const inc = incoming.value
    if (!inc) return
    // Double-click guard: without this two rapid clicks on Accept each fire
    // a separate `captureLocal` (and thus a separate mic-permission prompt)
    // before `incoming.value` is cleared.
    if (callStarting || active.value) return
    callStarting = true
    useChatAudio().stopCallRingtone()
    if (autoRejectTimer) { clearTimeout(autoRejectTimer); autoRejectTimer = null }

    try {
        let localStream: MediaStream
        try {
            localStream = await captureLocal(inc.kind)
        } catch (err: any) {
            // Mic/cam permission denied, no device, or call_requires_https.
            // Tell the caller so their ringing stops cleanly instead of timing
            // out 30 s later, then dismiss our own incoming dialog (cleared via
            // `incoming.value = null`) and surface the error for a toast.
            error.value = err?.name && err.name !== 'Error' ? err.name : (err?.message || 'media_unavailable')
            try {
                useChatSocket().send({ type: 'call.reject', callId: inc.callId, threadId: inc.threadId })
            } catch { /* socket may already be gone */ }
            incoming.value = null
            throw err
        }

        active.value = {
            callId: inc.callId,
            threadId: inc.threadId,
            threadKind: inc.threadKind,
            kind: inc.kind,
            status: 'connecting',
            remotes: new Map(),
            localStream,
            screenStream: null,
            muted: false,
            cameraOff: false,
            screenSharing: false,
            startedAt: Date.now()
        }
        incoming.value = null

        try {
            await connectMediasoup(inc.threadId)
            await publishLocalTracks(localStream, inc.kind)
            if (active.value) active.value = { ...active.value, status: 'active' }
        } catch (err: any) {
            console.error('[chat:call] accept setup failed', err)
            error.value = err?.message || 'setup_failed'
            // Tell the caller we couldn't pick up so their ringing stops cleanly
            // instead of timing out 30s later.
            try {
                useChatSocket().send({ type: 'call.reject', callId: inc.callId, threadId: inc.threadId })
            } catch { /* socket may already be gone */ }
            teardown()
        }
    } finally {
        callStarting = false
    }
}

function reject() {
    const inc = incoming.value
    if (!inc) return
    useChatSocket().send({ type: 'call.reject', callId: inc.callId, threadId: inc.threadId })
    incoming.value = null
    useChatAudio().stopCallRingtone()
    if (autoRejectTimer) { clearTimeout(autoRejectTimer); autoRejectTimer = null }
}

function hangup() {
    const state = active.value
    if (!state) return
    // Server's chat WS handler only knows `call.initiate` / `call.cancel` /
    // `call.reject` — post-connect hangup re-uses the same cancel frame.
    useChatSocket().send({
        type: 'call.cancel',
        callId: state.callId,
        threadId: state.threadId
    })
    teardown()
}

function teardown() {
    if (autoRejectTimer) { clearTimeout(autoRejectTimer); autoRejectTimer = null }
    // Stop local tracks (kills the browser's mic/cam indicator)
    const state = active.value
    if (state?.localStream) {
        for (const t of state.localStream.getTracks()) { try { t.stop() } catch {} }
    }
    if (state?.screenStream) {
        for (const t of state.screenStream.getTracks()) { try { t.stop() } catch {} }
    }
    try { sendTransport?.close() } catch {}
    try { recvTransport?.close() } catch {}
    sendTransport = null
    recvTransport = null
    try { screenProducer?.close() } catch {}
    screenProducer = null
    try { mediasoupWs?.close() } catch {}
    mediasoupWs = null
    mediasoupDevice = null
    pending.forEach(p => p.reject(new Error('teardown')))
    pending.clear()
    active.value = null
}

/* ───────────────── Local media ───────────────── */

async function captureLocal(kind: 'audio' | 'video'): Promise<MediaStream> {
    // getUserMedia requires a secure context (HTTPS or localhost). On the
    // HTTP dev host `navigator.mediaDevices` is undefined — fail loudly here
    // so the UI can surface a clear message instead of a raw TypeError.
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('call_requires_https')
    }
    // Pull the user's preferred mic from chatProfile. Reading via the session
    // composable here avoids forcing every caller (incoming-call accept,
    // outgoing-call start, mid-call camera toggle) to plumb the device id.
    // Empty / null falls through to the browser default.
    //
    // Mid-call device switching is not supported here — captureLocal runs
    // once at call start. A future improvement would `replaceTrack` on the
    // outgoing producers when the user picks a new mic during a call.
    const { user } = useUserSession()
    const profile: any = (user?.value as any)?.chatProfile || {}
    const inputDeviceId: string | null = profile.audioInputDeviceId || null

    const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
    }
    // `exact` rather than `ideal` so the call honours the user's pick or
    // throws OverconstrainedError — better than silently grabbing the
    // wrong mic. Caller catches and surfaces the error in the UI.
    if (inputDeviceId) audioConstraints.deviceId = { exact: inputDeviceId }

    const constraints: MediaStreamConstraints = { audio: audioConstraints }
    if (kind === 'video') {
        constraints.video = { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
    }
    try {
        return await navigator.mediaDevices.getUserMedia(constraints)
    } catch (err: any) {
        // OverconstrainedError fires when the saved deviceId is no longer
        // present (e.g. the USB headset was unplugged since the user saved
        // the preference). Retry once with the system default so the call
        // still goes through; the user can re-pick in settings later.
        if (err?.name === 'OverconstrainedError' && inputDeviceId) {
            console.warn('[chat:call] saved audioInputDeviceId is no longer available — falling back to default')
            const fallback = constraints
            if (fallback.audio && typeof fallback.audio === 'object') {
                delete (fallback.audio as MediaTrackConstraints).deviceId
            }
            const stream = await navigator.mediaDevices.getUserMedia(fallback)
            // Clear the dead deviceId server-side so future calls don't repeat
            // the constraint→fail→fallback dance and the settings dropdown
            // stops advertising a phantom device. Best-effort: a failure
            // here is non-fatal and gets surfaced only in the console.
            $fetch('/api/chat/settings', {
                method: 'PUT',
                body: { audioInputDeviceId: null }
            }).catch(e => console.warn('[chat:call] failed to clear stale audioInputDeviceId', e))
            return stream
        }
        throw err
    }
}

function toggleMute(): boolean {
    const state = active.value
    if (!state?.localStream) return false
    const next = !state.muted
    for (const t of state.localStream.getAudioTracks()) t.enabled = !next
    active.value = { ...state, muted: next }
    return next
}

function toggleCamera(): boolean {
    const state = active.value
    if (!state?.localStream) return false
    const next = !state.cameraOff
    for (const t of state.localStream.getVideoTracks()) t.enabled = !next
    active.value = { ...state, cameraOff: next }
    return next
}

async function toggleScreenShare(): Promise<boolean> {
    const state = active.value
    if (!state) return false

    if (state.screenSharing) {
        // Stop sharing: close the existing screen producer + stream
        if (state.screenStream) {
            for (const t of state.screenStream.getTracks()) { try { t.stop() } catch {} }
        }
        // Tell mediasoup to drop the producer so remote consumers get
        // `consumerClosed` and can tear down their tiles immediately,
        // instead of waiting for the track-ended heuristic to kick in.
        if (screenProducer) {
            const pid = screenProducer.id
            try { screenProducer.close() } catch {}
            screenProducer = null
            rpc('closeProducer', { producerId: pid }).catch(err =>
                console.warn('[chat:call] closeProducer rpc failed', err)
            )
        }
        active.value = { ...state, screenStream: null, screenSharing: false }
        return false
    }

    const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15 } },
        audio: false
    })
    // User can end sharing via the browser chrome — wire the track's `ended`
    // event back through us so the UI flips the toggle off cleanly.
    const screenTrack = screenStream.getVideoTracks()[0]
    if (screenTrack) {
        screenTrack.addEventListener('ended', () => {
            if (active.value?.screenSharing) toggleScreenShare()
        })
    }

    // If the call ended while we were awaiting the picker, drop the stream
    // and bail — otherwise we'd resurrect a stale `active` state below and
    // leak the captured display surface.
    if (!active.value || !sendTransport || !screenTrack) {
        for (const t of screenStream.getTracks()) { try { t.stop() } catch {} }
        return false
    }

    try {
        screenProducer = await sendTransport.produce({
            track: screenTrack,
            encodings: [{ maxBitrate: 1_500_000 }],
            appData: { source: 'screen' }
        })
    } catch (err) {
        console.error('[chat:call] screen produce failed', err)
        for (const t of screenStream.getTracks()) { try { t.stop() } catch {} }
        return false
    }

    // Re-check after the second await — a hangup between produce() and the
    // state flip would otherwise leave `screenProducer` set without
    // `screenSharing` true, blocking the stop-share button.
    if (!active.value) {
        try { screenProducer?.close() } catch { /* noop */ }
        screenProducer = null
        for (const t of screenStream.getTracks()) { try { t.stop() } catch {} }
        return false
    }

    active.value = { ...active.value, screenStream, screenSharing: true }
    return true
}

/* ───────────────── Mediasoup wiring ───────────────── */

async function connectMediasoup(threadId: string): Promise<void> {
    // 1. Mint the ticket via the main app
    const tokenRes = await $fetch<{ token: string; mediasoupUrl: string; roomId: string }>(
        '/api/chat/media/token',
        { method: 'POST', body: { threadId } }
    )

    // 2. Open the mediasoup WS
    mediasoupWs = new WebSocket(`${tokenRes.mediasoupUrl}?ticket=${encodeURIComponent(tokenRes.token)}`)

    await new Promise<void>((resolve, reject) => {
        mediasoupWs!.addEventListener('open', () => resolve(), { once: true })
        mediasoupWs!.addEventListener('error', () => reject(new Error('mediasoup_ws_error')), { once: true })
    })

    // Per-frame dispatch
    mediasoupWs.addEventListener('message', (ev) => {
        let frame: any
        try { frame = JSON.parse(typeof ev.data === 'string' ? ev.data : '') } catch { return }
        if (!frame) return

        if (frame.type === 'response' && pending.has(frame.id)) {
            const p = pending.get(frame.id)!
            pending.delete(frame.id)
            if (frame.error) p.reject(new Error(frame.error))
            else p.resolve(frame.data)
            return
        }
        // Notifies
        switch (frame.type) {
            case 'welcome':
                // Already handled via the explicit `getRouterRtpCapabilities`
                // RPC below; the welcome carries existingPeers for eager
                // consume — we'll iterate those after device is loaded.
                handleWelcome(frame).catch(err =>
                    console.error('[chat:call] welcome handler failed', err)
                )
                break
            case 'newProducer':
                consumeProducer(frame.userId, frame.producerId, frame.appData || {}).catch(err =>
                    console.error('[chat:call] consume failed', err)
                )
                break
            case 'peerJoined':
                if (active.value) {
                    const rem = active.value.remotes
                    if (!rem.has(frame.userId)) {
                        rem.set(frame.userId, emptyRemote(frame.userId))
                        active.value = { ...active.value, remotes: new Map(rem) }
                    }
                }
                break
            case 'peerLeft':
                if (active.value) {
                    active.value.remotes.delete(frame.userId)
                    active.value = { ...active.value, remotes: new Map(active.value.remotes) }
                }
                break
            case 'consumerClosed': {
                // Server killed one consumer (peer stopped a producer mid-call,
                // e.g. screen-share off). Detach that single track from the
                // owning remote so the tile updates without dropping the peer.
                const consumerId: string = frame.consumerId
                if (!consumerId || !active.value) break
                const remotes = new Map(active.value.remotes)
                for (const [userId, r] of remotes) {
                    const idx = r.consumerIds.indexOf(consumerId)
                    if (idx < 0) continue
                    const slot = r.consumerSlot[consumerId]
                    const next: RemotePeer = {
                        ...r,
                        consumerIds: r.consumerIds.filter(id => id !== consumerId),
                        consumerSlot: { ...r.consumerSlot }
                    }
                    delete next.consumerSlot[consumerId]
                    // Drop the corresponding stream slot. Re-creating the
                    // MediaStream is fine here; we don't currently track per-
                    // consumer track refs, and the slot only ever has one
                    // track (audio/video/screen each get their own consumer).
                    if (slot === 'audio') {
                        if (next.audioStream) {
                            for (const t of next.audioStream.getTracks()) { try { t.stop() } catch {} }
                        }
                        next.audioStream = null
                        next.hasAudio = false
                    } else if (slot === 'video') {
                        if (next.videoStream) {
                            for (const t of next.videoStream.getTracks()) { try { t.stop() } catch {} }
                        }
                        next.videoStream = null
                        next.hasVideo = false
                    } else if (slot === 'screen') {
                        if (next.screenStream) {
                            for (const t of next.screenStream.getTracks()) { try { t.stop() } catch {} }
                        }
                        next.screenStream = null
                        next.hasScreen = false
                    }
                    // If the peer no longer publishes anything, drop the tile
                    if (next.consumerIds.length === 0) {
                        remotes.delete(userId)
                    } else {
                        remotes.set(userId, next)
                    }
                    active.value = { ...active.value, remotes }
                    break
                }
                break
            }
        }
    })

    mediasoupWs.addEventListener('close', () => {
        // Unexpected disconnect mid-call → end the call cleanly
        if (active.value?.status === 'active' || active.value?.status === 'connecting') {
            error.value = 'media_disconnected'
            teardown()
        }
    })

    // 3. Load mediasoup-client Device with the router's capabilities
    const { Device } = await import('mediasoup-client')
    mediasoupDevice = new Device()
    const routerRtpCapabilities = await rpc('getRouterRtpCapabilities')
    await mediasoupDevice.load({ routerRtpCapabilities })

    // 4. Create transports (send + recv)
    const iceServers = await fetchIceServers()
    const sendParams = await rpc('createTransport', { direction: 'send' })
    sendTransport = mediasoupDevice.createSendTransport({
        id: sendParams.id,
        iceParameters: sendParams.iceParameters,
        iceCandidates: sendParams.iceCandidates,
        dtlsParameters: sendParams.dtlsParameters,
        iceServers
    })
    sendTransport.on('connect', ({ dtlsParameters }: any, callback: any, errback: any) => {
        rpc('connectTransport', { transportId: sendTransport!.id, dtlsParameters })
            .then(() => callback())
            .catch(errback)
    })
    sendTransport.on('produce', ({ kind, rtpParameters, appData }: any, callback: any, errback: any) => {
        rpc('produce', { transportId: sendTransport!.id, kind, rtpParameters, appData })
            .then((res: any) => callback({ id: res.producerId }))
            .catch(errback)
    })

    const recvParams = await rpc('createTransport', { direction: 'recv' })
    recvTransport = mediasoupDevice.createRecvTransport({
        id: recvParams.id,
        iceParameters: recvParams.iceParameters,
        iceCandidates: recvParams.iceCandidates,
        dtlsParameters: recvParams.dtlsParameters,
        iceServers
    })
    recvTransport.on('connect', ({ dtlsParameters }: any, callback: any, errback: any) => {
        rpc('connectTransport', { transportId: recvTransport!.id, dtlsParameters })
            .then(() => callback())
            .catch(errback)
    })
}

async function fetchIceServers(): Promise<RTCIceServer[]> {
    try {
        const res = await $fetch<{ iceServers: RTCIceServer[] }>('/api/chat/ice-servers')
        return res.iceServers || []
    } catch (err) {
        // Surface the failure instead of silently degrading — without ICE
        // servers most calls behind NAT will never connect, so it's better
        // to fail fast here and let the caller show an error.
        console.error('[chat:call] fetchIceServers failed', err)
        throw new Error('ice_servers_unavailable')
    }
}

async function publishLocalTracks(stream: MediaStream, kind: 'audio' | 'video'): Promise<void> {
    const audioTrack = stream.getAudioTracks()[0]
    if (audioTrack) {
        await sendTransport.produce({
            track: audioTrack,
            codecOptions: { opusStereo: true, opusDtx: true },
            appData: { source: 'mic' }
        })
    }
    if (kind === 'video') {
        const videoTrack = stream.getVideoTracks()[0]
        if (videoTrack) {
            await sendTransport.produce({
                track: videoTrack,
                encodings: [{ maxBitrate: 800_000 }],
                appData: { source: 'cam' }
            })
        }
    }
}

async function handleWelcome(frame: any): Promise<void> {
    if (!frame?.existingPeers) return
    for (const ep of frame.existingPeers) {
        if (!active.value) continue
        if (!active.value.remotes.has(ep.userId)) {
            active.value.remotes.set(ep.userId, emptyRemote(ep.userId))
        }
        for (const prod of ep.producers || []) {
            await consumeProducer(ep.userId, prod.producerId, prod.appData || {})
        }
    }
    if (active.value) active.value = { ...active.value, remotes: new Map(active.value.remotes) }
}

async function consumeProducer(userId: string, producerId: string, appData: Record<string, any>): Promise<void> {
    if (!mediasoupDevice || !recvTransport) return
    const res = await rpc('consume', {
        producerId,
        rtpCapabilities: mediasoupDevice.rtpCapabilities
    })
    const consumer = await recvTransport.consume({
        id: res.consumerId,
        producerId: res.producerId,
        kind: res.kind,
        rtpParameters: res.rtpParameters,
        appData: res.appData
    })

    // Wire into the remote peer state
    if (!active.value) return
    const remotes = new Map(active.value.remotes)
    const existing = remotes.get(userId) || emptyRemote(userId)
    const track = consumer.track

    const source = (res.appData?.source || appData?.source) as string | undefined
    let slot: 'audio' | 'video' | 'screen'
    if (res.kind === 'audio') {
        const s = existing.audioStream || new MediaStream()
        s.addTrack(track)
        existing.audioStream = s
        existing.hasAudio = true
        slot = 'audio'
    } else if (source === 'screen') {
        const s = existing.screenStream || new MediaStream()
        s.addTrack(track)
        existing.screenStream = s
        existing.hasScreen = true
        slot = 'screen'
    } else {
        const s = existing.videoStream || new MediaStream()
        s.addTrack(track)
        existing.videoStream = s
        existing.hasVideo = true
        slot = 'video'
    }

    // Remember which consumerId fed which slot so a `consumerClosed`
    // frame from mediasoup can tear down just that track.
    existing.consumerIds = [...existing.consumerIds, res.consumerId]
    existing.consumerSlot = { ...existing.consumerSlot, [res.consumerId]: slot }

    remotes.set(userId, { ...existing })
    active.value = { ...active.value, remotes }
}

function emptyRemote(userId: string): RemotePeer {
    return {
        userId,
        audioStream: null, videoStream: null, screenStream: null,
        hasAudio: false, hasVideo: false, hasScreen: false,
        consumerIds: [],
        consumerSlot: {}
    }
}

/* ───────────────── Page-unload cleanup ───────────────── */

function sendCancelOnUnload() {
    const state = active.value
    if (!state) return
    // Two-pronged: queue a WS frame (delivered if the socket flushes in
    // time) AND fire a sendBeacon to the HTTP endpoint (mandated delivery).
    // Without the beacon the peer can ring for the full 30 s auto-reject
    // window when a tab is closed mid-call — browsers will drop pending
    // WS sends as soon as the page enters the terminated state.
    try {
        useChatSocket().send({
            type: 'call.cancel',
            callId: state.callId,
            threadId: state.threadId
        })
    } catch { /* socket may already be gone */ }
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const body = JSON.stringify({ callId: state.callId, threadId: state.threadId })
            navigator.sendBeacon('/api/chat/call/cancel', new Blob([body], { type: 'application/json' }))
        }
    } catch { /* noop — beacons are best-effort */ }
    teardown()
}

if (typeof window !== 'undefined') {
    // `beforeunload` fires on most desktop tab closes; `pagehide` fires on
    // mobile Safari and bfcache evictions where `beforeunload` is often
    // suppressed. Register both so neither platform leaves callees ringing.
    window.addEventListener('beforeunload', sendCancelOnUnload)
    window.addEventListener('pagehide', sendCancelOnUnload)
}

/* ───────────────── Public API ───────────────── */

export function useCall() {
    wire()
    return {
        incoming: computed(() => incoming.value),
        active: computed(() => active.value),
        error: computed(() => error.value),
        isBusy: computed(() => !!active.value && active.value.status !== 'idle'),
        start,
        accept,
        reject,
        hangup,
        toggleMute,
        toggleCamera,
        toggleScreenShare
    }
}
